import React from 'react';
import type { UserDraft } from '../../components/CreateUpdateUserModal/component';
import { Title, Loader, UserRoleChip } from '../../elements';
import { notifications, extractApiErrorMessage } from '../../utils';
import {
  Grid,
  Paper,
  TableBody,
  TableCell,
  TableRow,
  Table,
  Icon,
  Link,
  Button,
  Container,
  Tooltip,
  Chip
} from '@material-ui/core';
import { LoaderSize } from '../../constants/constants';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import {
  CreateUpdateUserModal,
  ChangePasswordModal,
  UserActionLogView,
  UserActionLogViewHandle
} from '../../components';
import useAuthContext from '../../hooks/useAuthContext';
import { useParams } from 'react-router-dom';
import UserRole from '../../constants/user-role';
import { ApiService } from '../../hooks';
import { HealthSystemDomainUsersResultUserViewModel } from '../../api/HealthSystemApi';

// Invite mode for CreateUpdateUserModal: no id, nothing filled in yet.
const EMPTY_USER: UserDraft = {};

const UserView: React.FC<any> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const { id } = useParams();

  const apiService = new ApiService();

  const [user, setUser] = React.useState<HealthSystemDomainUsersResultUserViewModel | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(true);
  const [updateUserDialogOpen, setUpdateUserDialogOpen] = React.useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = React.useState(false);

  const loadUserModel = async () => {
    setIsBusy(true);
    setLoaded(false);

    if (!id) {
      notifications.error('User ID is undefined.');
      return;
    }

    await apiService.api.users
      .usersGetUserView({ Id: id })
      .then((result) => {
        setUser(result.data);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        setLoaded(false);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const userActionLogViewRef = React.useRef<UserActionLogViewHandle>(null!);

  React.useEffect(() => {
    loadUserModel();
    userActionLogViewRef.current.reload();
  }, [id]);

  const onUserUpdated = (result: boolean | HealthSystemDomainUsersResultUserViewModel | null) => {
    setUpdateUserDialogOpen(false);

    if (result === null) {
      return;
    }

    if (result === true) {
      loadUserModel();
    }
  };

  const canUpdateUser = () => {
    return state.userId === id || state.role === UserRole.manager || state.role === UserRole.admin;
  };

  const handleUpdateUserClick = () => {
    setUpdateUserDialogOpen(true);
  };

  return (
    <>
      <Container maxWidth="lg">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper className={classes.paper} elevation={1}>
              <Grid container spacing={1}>
                <Grid item md={12} id="userFieldsView">
                  <Grid container direction="row" alignItems="center" spacing={1}>
                    <Grid item id="userIcon">
                      <Icon color="primary" fontSize="large">
                        {'account_box'}
                      </Icon>
                    </Grid>

                    <Grid item>
                      <Title>{t('f-usr-view')}</Title>
                    </Grid>

                    {/* Only the exceptional states are called out; an ordinary active user gets no chip. */}
                    {user?.isDeactivated && (
                      <Grid item>
                        <Chip
                          size="small"
                          label={t('f-usr-status-deactivated')}
                          className="!bg-red-100 !text-red-800"
                        />
                      </Grid>
                    )}

                    {!user?.isDeactivated && !user?.isActivated && (
                      <Grid item>
                        <Chip
                          size="small"
                          label={t('f-usr-status-invited')}
                          className="!bg-amber-100 !text-amber-800"
                        />
                      </Grid>
                    )}

                    {canUpdateUser() && (
                      <Grid item className={classes.headerRightSection}>
                        <Button
                          id="updateUserBtn"
                          disabled={isBusy}
                          color="default"
                          size="small"
                          variant="contained"
                          startIcon={<Icon>{'edit'}</Icon>}
                          onClick={handleUpdateUserClick}
                        >
                          {t('f-com-update')}
                        </Button>
                      </Grid>
                    )}

                    {state.userId === id && (
                      <Grid item>
                        <Button
                          id="changePasswordBtn"
                          disabled={isBusy}
                          color="default"
                          size="small"
                          variant="contained"
                          startIcon={<Icon>{'lock'}</Icon>}
                          onClick={() => setChangePasswordDialogOpen(true)}
                        >
                          {t('f-account-change-password')}
                        </Button>
                      </Grid>
                    )}

                    {state.businessUnits?.length > 0 && state.userId === id && (
                      <Grid item>
                        <Tooltip arrow title={t('f-com-copy-video-link-tooltip')}>
                          <Button
                            id="copyVideoLinkBtn"
                            color="default"
                            size="small"
                            variant="contained"
                            startIcon={<Icon>{'videocam'}</Icon>}
                            onClick={() => {
                              navigator.clipboard.writeText('https://pighealthsystem.daily.co/SaUZf06r4V2DwJiHX92u');
                              notifications.success(t('f-com-copied'));
                            }}
                          >
                            {t('f-com-copy-video-link')}
                          </Button>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>

                  {loaded && user && (
                    <>
                      <Grid container spacing={1}>
                        <Grid item lg={7} md={8} xs={10}>
                          <Table size="small" aria-label="user data table" id="user-data-table">
                            <TableBody>
                              <TableRow key={'userName'}>
                                <TableCell align="right">{t('f-usr-name')}</TableCell>
                                <TableCell className={classes.bold}>{user.name}</TableCell>
                              </TableRow>

                              <TableRow key={'userEmail'}>
                                <TableCell align="right">{t('f-com-email')}</TableCell>
                                <TableCell>
                                  {
                                    <Link href={`mailto:${user.email}`} variant="body1">
                                      {user.email}
                                    </Link>
                                  }
                                </TableCell>
                              </TableRow>

                              <TableRow key={'animalType'}>
                                <TableCell align="right">{t('f-usr-role')}</TableCell>
                                <TableCell>{<UserRoleChip role={user.role ?? UserRole.user} />}</TableCell>
                              </TableRow>

                              <TableRow key={'userLan'}>
                                <TableCell align="right">{t('f-usr-lang')}</TableCell>
                                <TableCell className={classes.bold}>{user.languageIso}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>

                <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <UserActionLogView userId={id as string} ref={userActionLogViewRef} />
          </Grid>
        </Grid>
      </Container>

      <CreateUpdateUserModal
        key={updateUserDialogOpen ? 'add-new' : (user?.id ?? '')}
        opened={updateUserDialogOpen}
        returnObject={false}
        onModalAction={onUserUpdated}
        user={user ?? EMPTY_USER}
      />

      <ChangePasswordModal opened={changePasswordDialogOpen} onClose={() => setChangePasswordDialogOpen(false)} />
    </>
  );
};

export default UserView;
